import { parseDatingDate } from '@gene42/pedigree/pregnancy-dating';

import { partialDateToDto } from '@/features/patient-record/hooks/usePatientMutations';
import type { LifeStatus, PregnancyDatingBasis, SexCode } from '@/models/patient';

/**
 * PTV2-1328 — project the node-edit panel's Personal-info draft onto a PhenoTips-JSON demographics
 * `overlay` (the same channel `summaryToPhenotipsJSON.nodeToMember` emits on load). The engine's
 * `previewNodeDemographics(guid, overlay)` overlays this onto the live node and repaints it, so a
 * panel edit shows on the graph node BEFORE any save.
 *
 * Only the keys the Personal-info card owns are emitted; an omitted key means "clear it" (the engine
 * resets those keys before applying the overlay), so an emptied name/DOB/life-status removes its
 * label/glyph. Keys not owned here (disorders / genes / features / external_id) are preserved by the
 * engine and never touched.
 */

/** The subset of `PersonalDraft` that drives the graph node. */
export interface NodeDemographicsProjectionInput {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly dateOfBirth: string | null;
  readonly dateOfDeath: string | null;
  readonly sex: SexCode | null;
  readonly genderIdentity: string | null;
  /** "Same gender as sex" shortcut — mirrors sex into gender (as the save does). */
  readonly sameGenderAsSex: boolean;
  readonly lifeStatus: LifeStatus | null;
  /** PTV2-1586 dating pair — drives the fetal node's gestational-age / EDD label. */
  readonly pregnancyDatingAnchor: string | null;
  readonly pregnancyDatingBasis: PregnancyDatingBasis | null;
  readonly pregnancyDatingAsOf: string | null;
}

// LifeStatus → the engine's internal lowercase life-status string. ALIVE_AND_WELL is handled
// specially (alive + the aliveandwell flag); UNKNOWN/null are omitted so the engine defaults to
// alive — matching the load path (`summaryToPhenotipsJSON`), where UNKNOWN isn't in the map either.
const LIFE_STATUS_TO_INTERNAL: Partial<Record<LifeStatus, string>> = {
  ALIVE: 'alive',
  DECEASED: 'deceased',
  UNBORN: 'unborn',
  STILL_BORN: 'stillborn',
  MISCARRIAGE: 'miscarriage',
  ABORTED: 'aborted',
};

// GenderOptions code → the engine's internal glyph gender. The engine vocabulary is M/F/O/NB/U
// (Person.parseGender; NB and O are DISTINCT — see nodeMenuFields 'Non-binary' and mergeConflicts
// genderMap). 'unknown' → 'U'; anything unrecognised falls through to 'U' via parseGender anyway.
const GENDER_TO_INTERNAL: Record<string, string> = { M: 'M', F: 'F', O: 'O', nonBinary: 'NB', unknown: 'U' };

/** Default gender that "same as sex" derives (mirror of PersonalInfoCard.deriveGenderFromSex): sex
 *  letter codes map 1:1 to GenderOptions codes, except U → long-form 'unknown'. */
function deriveGenderFromSex(sex: SexCode | null): string | null {
  if (sex === null) return null;
  // U and the assigned-at-birth-only codes (INTERSEX/UNASSIGNED) have no gender-identity mirror, so
  // "same as sex" derives 'unknown' (a diamond); M/F/O map 1:1 to GenderOptions. (PTV2-1332)
  if (sex === 'U' || sex === 'INTERSEX' || sex === 'UNASSIGNED') return 'unknown';
  return sex;
}

export function projectNodeDemographics(input: NodeDemographicsProjectionInput): Record<string, unknown> {
  const overlay: Record<string, unknown> = {};

  // Sex — `SexCode` ('M'|'F'|'O'|'U') already matches the engine's internal glyph codes 1:1. Drives
  // the "assigned at birth" (AMAB/AFAB) text.
  if (input.sex !== null) {
    overlay.sex = input.sex;
  }

  // Gender — drives the node SHAPE (square/circle/diamond) and the Male/Female label. MUST be emitted:
  // if omitted, the engine derives gender FROM sex (populateSexAndGenderFromStruct), so e.g. sex=Other
  // would force a diamond mid-edit even though the saved gender is Male (a square). Derive it exactly
  // as the save does (toSavedVm): mirror sex when "same as sex" is on, else the explicit identity.
  const rawGender = input.sameGenderAsSex ? deriveGenderFromSex(input.sex) : input.genderIdentity;
  if (rawGender !== null && rawGender !== '') {
    overlay.gender = GENDER_TO_INTERNAL[rawGender] ?? 'U';
  }

  // Name — emit both halves (empty string for a cleared half) whenever either is set, mirroring
  // nodeToMember; omit entirely when both are empty so the on-node name label clears.
  const first = input.firstName ?? '';
  const last = input.lastName ?? '';
  if (first !== '' || last !== '') {
    overlay.patient_name = { first_name: first, last_name: last };
  }

  const dob = partialDateToDto(input.dateOfBirth);
  if (dob !== undefined) {
    overlay.date_of_birth = dob;
  }
  const dod = partialDateToDto(input.dateOfDeath);
  if (dod !== undefined) {
    overlay.date_of_death = dod;
  }

  // Life status — ALIVE_AND_WELL → alive + aliveandwell:true. When alive, always emit the flag (even
  // false) so switching A&W→ALIVE clears the "alive & well" glyph.
  if (input.lifeStatus === 'ALIVE_AND_WELL') {
    overlay.life_status = 'alive';
    overlay.aliveandwell = true;
  } else {
    const internal = input.lifeStatus === null ? undefined : LIFE_STATUS_TO_INTERNAL[input.lifeStatus];
    if (internal !== undefined) {
      overlay.life_status = internal;
    }
    if (overlay.life_status === 'alive') {
      overlay.aliveandwell = false;
    }
  }

  // Pregnancy dating (PTV2-1586) — emitted as a pair, and only for an unborn node, so the label
  // matches what the card lets the clinician author. An omitted anchor clears the label.
  const anchor = overlay.life_status === 'unborn' ? parseDatingDate(input.pregnancyDatingAnchor) : null;
  if (anchor !== null) {
    overlay.pregnancy_dating_anchor = anchor;
    overlay.pregnancy_dating_basis = input.pregnancyDatingBasis;
    const asOf = parseDatingDate(input.pregnancyDatingAsOf);
    if (asOf !== null) overlay.pregnancy_dating_as_of = asOf;
  }

  return overlay;
}

/**
 * PTV2-1391 — project the node-edit panel's Pedigree-attributes draft onto the engine's INTERNAL
 * pedigree-prop overlay (carrier / adopted / childless status + documented evaluation). Unlike the
 * demographics overlay (a PhenoTips-JSON `properties` subset), these are pedigree-only keys the engine
 * keeps in its internal property bag, so `previewNodePedigreeProps(guid, overlay)` writes them straight
 * onto the node and repaints the glyphs (carrier dot / adoption brackets / childless line / evaluated
 * asterisk) BEFORE any save. Value formats mirror the load path (`summaryToPhenotipsJSON`). Only SET
 * fields are emitted; an omitted key means "clear it" (the engine resets absent keys), so deselecting
 * a toggle removes its glyph.
 */
export interface NodePedigreePropsProjectionInput {
  readonly carrierStatus: string | null;
  readonly adoptedStatus: string | null;
  readonly childlessStatus: string | null;
  readonly childlessReason: string | null;
  readonly evaluated: boolean | null;
}

// VM enum (UPPERCASE, e.g. CarrierStatus.name()) → the engine's internal pedigree-prop strings.
const CARRIER_TO_INTERNAL: Record<string, string> = {
  CARRIER: 'carrier',
  UNCERTAIN: 'uncertain',
  PRESYMPTOMATIC: 'presymptomatic',
  AFFECTED: 'affected',
};
const ADOPTED_TO_INTERNAL: Record<string, string> = { ADOPTED_IN: 'adoptedIn', ADOPTED_OUT: 'adoptedOut' };
const CHILDLESS_TO_INTERNAL: Record<string, string> = { CHILDLESS: 'childless', INFERTILE: 'infertile' };

export function projectNodePedigreeProps(
  input: NodePedigreePropsProjectionInput,
): Record<string, unknown> {
  const overlay: Record<string, unknown> = {};
  if (input.carrierStatus !== null && CARRIER_TO_INTERNAL[input.carrierStatus] !== undefined) {
    overlay.carrierStatus = CARRIER_TO_INTERNAL[input.carrierStatus];
  }
  if (input.adoptedStatus !== null && ADOPTED_TO_INTERNAL[input.adoptedStatus] !== undefined) {
    overlay.adoptedStatus = ADOPTED_TO_INTERNAL[input.adoptedStatus];
  }
  if (input.childlessStatus !== null && CHILDLESS_TO_INTERNAL[input.childlessStatus] !== undefined) {
    overlay.childlessStatus = CHILDLESS_TO_INTERNAL[input.childlessStatus];
    // PTV2-1584: the bracketed note the engine draws under the node. Always published alongside the
    // status (empty string clears it) so deleting the text removes the label live, rather than
    // leaving the previous note on the node until the next reload.
    overlay.childlessReason = input.childlessReason ?? '';
  }
  if (input.evaluated === true) {
    overlay.evaluated = true;
  }
  return overlay;
}
